import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import {
  DsBoxModule,
  DsButtonModule,
  DsCheckboxModule, DsEmptyStateModule,
  DsFormFieldModule,
  DsIconModule,
  DsImprintModule,
  DsLoadingAreaConfiguration,
  DsLoadingAreaModule,
  DsLoadingAreaService,
  DsModalComponent,
  DsModalModule,
  DsPopoverModule,
  DsProgressCircleModule,
  DsSelectModule,
  DsTabsModule,
  DsToastModel,
  DsToastService,
} from '@bmw-ds/components';
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DealerBiddingService } from '../../services/bidding.service';
import {
  AuctionVehicle,
  CompletedVehicle,
  PurchasingVehicle,
  Vehicle,
} from '../../models/vehicle.model';
import { BidRequest } from '../../models/bid.model';
import { Clipboard } from '@angular/cdk/clipboard';
import { WatchItem } from '../../models/watchlist.model';
import { TransunionService } from '../../services/transunion.service';
import {
  VehicleCheckDetaisRequest,
  VehicleCheckDetaisResponse,
} from '../../models/vehicleCheckDetais';
import { RoleService } from '../../services/role.service';
import { UserStateService } from '../../services/user-state.service';
import { catchError, map } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';

interface WatchlistVehicle {
  brand: string;
  model: string;
  adgCode: string;
  reservePrice: number;
  reconPrice: number;
  mileage: number;
  year: number;
}

interface VehicleFile {
  XSTRING: string;
  FILENAME: string;
}

@Component({
  selector: 'app-stock-search',
  imports: [
    CommonModule,
    DsBoxModule,
    DsImprintModule,
    RouterModule,
    DsCheckboxModule,
    DsFormFieldModule,
    DsSelectModule,
    DsProgressCircleModule,
    DsLoadingAreaModule,
    FormsModule,
    DsIconModule,
    DsPopoverModule,
    DsModalModule,
    DsButtonModule,
    DsTabsModule,
    DsEmptyStateModule,
  ],
  templateUrl: './stock-search.component.html',
  styleUrl: './stock-search.component.scss',
})
export class StockSearchComponent implements OnInit {
  selectedBrand = signal<string | null>(null);
  selectedSeriesVariant = signal<string | null>(null);
  searchClicked = true;
  // private countdownInterval: any;

  loadingAreaConfig: DsLoadingAreaConfiguration = { id: 'bodyLoading' };

  private cdr = inject(ChangeDetectorRef);
  private readonly loadingAreaService = inject(DsLoadingAreaService);
  private readonly dbConfigService = inject(DealerBiddingService);
  private readonly transunionService = inject(TransunionService);
  private readonly toastService = inject(DsToastService);
  private readonly roleService = inject(RoleService);
  private readonly userStateService = inject(UserStateService);
  private readonly router = inject(Router);
  private readonly clipboard = inject(Clipboard);
  newApplicationModalOpen = false;
  watchlistModalIsOpen: boolean = false;
  vehicleTypes: any[] = [];
  modelYearOptions: any[] = [];
  brandOptions: any[] = [];
  modelOptions: any[] = [];
  fuelTypeOptions: any[] = [];
  transmissionTypeOptions: any[] = [];
  variantOptions: any[] = [];
  canView = false;
  canBid = false;
  canLoad = false;
  canManage = false;

  isMatchBidExpanded: boolean = true; // Default to expanded
  isAwaitingOrderExpanded: boolean = true; // Default to expanded

  overviewModalIsOpen: boolean = false;
  // near existing interval fields
  // private countdownInterval: any;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private auctionRefreshInterval: ReturnType<typeof setInterval> | null = null;
  private readonly auctionAutoRefreshMs = 10000;

  toggleMatchBidAccordion(): void {
    this.isMatchBidExpanded = !this.isMatchBidExpanded;
  }

  toggleAwaitingOrderAccordion(): void {
    this.isAwaitingOrderExpanded = !this.isAwaitingOrderExpanded;
  }
  async ngOnInit() {
    this.canView = this.roleService.canView();
    this.canBid = this.roleService.canBid();
    this.canLoad = this.roleService.canLoad();
    this.canManage = this.roleService.canManage();
    this.loadActiveVehicles(true);
    this.startAuctionAutoRefresh();
  }
  private startAuctionAutoRefresh(): void {
    this.stopAuctionAutoRefresh();

    this.auctionRefreshInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.loadActiveVehicles(false);
      }
    }, this.auctionAutoRefreshMs);
  }
  private stopAuctionAutoRefresh(): void {
    if (this.auctionRefreshInterval) {
      clearInterval(this.auctionRefreshInterval);
      this.auctionRefreshInterval = null;
    }
  }
  private loadActiveVehicles(showLoader: boolean = false): void {
    const auctionLoadingArea: DsLoadingAreaConfiguration = {
      id: 'bodyLoading',
      loadingTitle: 'Loading',
      loadingText: 'Active Auction Vehicles',
    };

    if (showLoader) {
      this.loadingAreaService.addLoadingArea(auctionLoadingArea);
    }

    this.dbConfigService.getActiveVehicles().subscribe({
      next: async (response) => {
        if (!response.success) {
          const errorMessage =
            response.error?.message || 'Failed to load active vehicles.';
          this.addToast('critical', errorMessage);
          this.vehicles = [];
          this.cdr.detectChanges();
          if (showLoader) {
            this.loadingAreaService.removeLoadingArea(auctionLoadingArea.id);
          }
          return;
        }

        const backendVehicles = response.data ?? [];
        this.vehicles = await Promise.all(
          backendVehicles.map(async (v) => {
            const loadedTime = v.loadedTime.trim();
            const expiryTime = v.expiryTime.trim();

            const loadedTimestamp = new Date(
              Number(loadedTime.substring(0, 4)),
              Number(loadedTime.substring(4, 6)) - 1,
              Number(loadedTime.substring(6, 8)),
              Number(loadedTime.substring(8, 10)),
              Number(loadedTime.substring(10, 12)),
              Number(loadedTime.substring(12, 14))
            ).getTime();

            const expiryTimestamp = new Date(
              Number(expiryTime.substring(0, 4)),
              Number(expiryTime.substring(4, 6)) - 1,
              Number(expiryTime.substring(6, 8)),
              Number(expiryTime.substring(8, 10)),
              Number(expiryTime.substring(10, 12)),
              Number(expiryTime.substring(12, 14))
            ).getTime();

            const now = Date.now();
            const remainingTimeMs = Math.max(expiryTimestamp - now, 0);

            return {
              id: v.vin,
              brand: v.brand,
              description: v.variant,
              modelYear: Number(v.modelYear),
              location: '',
              remainingTimeMs,
              remainingTimeDisplay:
                remainingTimeMs > 0
                  ? this.formatTime(remainingTimeMs)
                  : 'ENDED',
              reservePrice: v.reservePrice,
              reconPrice: v.reconPrice,
              currentBid: v.reservePrice,
              vehicleImage: '',
              loadedDealerCode: v.loadedDealerCode,
              highestBid: v.highestBid,
              photographS3Keys: v.photographS3Keys,
              photographsList: v.photographsList,
              conditionReportS3Key: v.conditionReportS3Key,
              conditionReportList: v.conditionReportList,
              loadedDealerName: v.loadedDealerName,
            };
          })
        );

        this.cdr.detectChanges();
        if (showLoader) {
          this.loadingAreaService.removeLoadingArea(auctionLoadingArea.id);
        }
      },
      error: (err) => {
        console.error('Failed to load vehicles', err);
        this.cdr.detectChanges();
        if (showLoader) {
          this.loadingAreaService.removeLoadingArea(auctionLoadingArea.id);
        }
      },
      complete: () => {
        this.cdr.detectChanges();
        if (showLoader) {
          this.loadingAreaService.removeLoadingArea(auctionLoadingArea.id);
        }
      },
    });
  }
  async fileToBase64(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
  mapToVehicleFileArray(
    arr: { XSTRING: string; FILENAME: string }[]
  ): VehicleFile[] {
    return arr.map((item) => ({
      FILENAME: item.FILENAME,
      XSTRING: item.XSTRING,
    }));
  }
  downloadBase64Document(conditionReports: VehicleFile[] | undefined) {
    if (conditionReports) {
      const decodedDoc = atob(conditionReports[0].XSTRING);

      const byteArray = new Uint8Array(decodedDoc.length);
      for (let i = 0; i < decodedDoc.length; i++) {
        byteArray[i] = decodedDoc.charCodeAt(i);
      }
      const blob = new Blob([byteArray], {
        type: 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      // const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = conditionReports[0].FILENAME || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } // Decode the Base64 string
  }

  @ViewChild('tpl') modal!: DsModalComponent;

  protected readonly Array = Array;

  openWatchlistModal() {
    this.watchlistModalIsOpen = true;
    this.transunionService.getVehicleTypes().subscribe({
      next: (response) => {
        if (!response.success) {
          this.addToast(
            'critical',
            response.error?.message || 'Failed to load vehicle types'
          );
          return;
        }
        if (response.data?.vehtypeList?.lookupType) {
          this.vehicleTypes = response.data.vehtypeList.lookupType.map(
            (type: any) => ({
              id: type.code,
              label: type.description,
              value: type.code,
            })
          );
        }
      },
      error: () => {
        this.addToast('critical', 'Failed to load vehicle types');
      },
    });
  }

  closeWatchlistModal() {
    this.watchlistModalIsOpen = false;
    this.resetWatchlistForm();
  }

  submitWatchlist() {
    const agent = this.userStateService.userId();
    const agentCode = localStorage.getItem('dealerCode') || '';

    const missingFields: string[] = [];
    if (!agent) missingFields.push('user');
    if (!agentCode) missingFields.push('dealer code');
    if (!this.watchlistForm.vehicleType) missingFields.push('vehicle type');
    if (!this.watchlistForm.modelYear) missingFields.push('model year');
    if (!this.watchlistForm.brand) missingFields.push('brand');
    if (!this.watchlistForm.model) missingFields.push('model');
    if (!this.watchlistForm.variant) missingFields.push('variant');

    const effectiveAdgCode =
      this.watchlistForm.adgCode?.trim() ||
      (typeof this.watchlistForm.variant === 'string'
        ? this.watchlistForm.variant.trim()
        : '');

    if (!effectiveAdgCode) missingFields.push('ADG/MM code');

    // Range validations (only validate if a value was provided)
    const minReserveProvided =
      String(this.watchlistForm.minReservePrice ?? '').trim() !== '';
    const maxReserveProvided =
      String(this.watchlistForm.maxReservePrice ?? '').trim() !== '';
    const minReconProvided =
      String(this.watchlistForm.minReconPrice ?? '').trim() !== '';
    const maxReconProvided =
      String(this.watchlistForm.maxReconPrice ?? '').trim() !== '';
    const minMileageProvided =
      String(this.watchlistForm.minMileage ?? '').trim() !== '';
    const maxMileageProvided =
      String(this.watchlistForm.maxMileage ?? '').trim() !== '';
    const minYearProvided =
      String(this.watchlistForm.minYear ?? '').trim() !== '';
    const maxYearProvided =
      String(this.watchlistForm.maxYear ?? '').trim() !== '';

    const minReserve = Number(this.watchlistForm.minReservePrice);
    const maxReserve = Number(this.watchlistForm.maxReservePrice);
    const minRecon = Number(this.watchlistForm.minReconPrice);
    const maxRecon = Number(this.watchlistForm.maxReconPrice);
    const minMileage = Number(this.watchlistForm.minMileage);
    const maxMileage = Number(this.watchlistForm.maxMileage);
    const minYear = Number(this.watchlistForm.minYear);
    const maxYear = Number(this.watchlistForm.maxYear);

    if (minReserveProvided && Number.isNaN(minReserve))
      missingFields.push('min reserve price (invalid)');
    if (maxReserveProvided && Number.isNaN(maxReserve))
      missingFields.push('max reserve price (invalid)');
    if (
      minReserveProvided &&
      maxReserveProvided &&
      !Number.isNaN(minReserve) &&
      !Number.isNaN(maxReserve) &&
      minReserve > maxReserve
    ) {
      missingFields.push('reserve price range (min > max)');
    }

    if (minReconProvided && Number.isNaN(minRecon))
      missingFields.push('min recon price (invalid)');
    if (maxReconProvided && Number.isNaN(maxRecon))
      missingFields.push('max recon price (invalid)');
    if (
      minReconProvided &&
      maxReconProvided &&
      !Number.isNaN(minRecon) &&
      !Number.isNaN(maxRecon) &&
      minRecon > maxRecon
    ) {
      missingFields.push('recon price range (min > max)');
    }

    if (minMileageProvided && Number.isNaN(minMileage))
      missingFields.push('min mileage (invalid)');
    if (maxMileageProvided && Number.isNaN(maxMileage))
      missingFields.push('max mileage (invalid)');
    if (
      minMileageProvided &&
      maxMileageProvided &&
      !Number.isNaN(minMileage) &&
      !Number.isNaN(maxMileage) &&
      minMileage > maxMileage
    ) {
      missingFields.push('mileage range (min > max)');
    }

    if (minYearProvided && Number.isNaN(minYear))
      missingFields.push('min year (invalid)');
    if (maxYearProvided && Number.isNaN(maxYear))
      missingFields.push('max year (invalid)');
    if (
      minYearProvided &&
      maxYearProvided &&
      !Number.isNaN(minYear) &&
      !Number.isNaN(maxYear) &&
      minYear > maxYear
    ) {
      missingFields.push('year range (min > max)');
    }

    if (missingFields.length > 0) {
      this.addToast(
        'critical',
        `Please complete/fix: ${missingFields.join(', ')}.`
      );
      return;
    }

    const watchItem: WatchItem = {
      agent,
      agentCode,
      brand: this.watchlistForm.brand ?? '',
      model: this.watchlistForm.model ?? '',
      adgCode: effectiveAdgCode,
      minReservePrice: minReserveProvided ? minReserve : 0,
      maxReservePrice: maxReserveProvided ? maxReserve : 0,
      minReconPrice: minReconProvided ? minRecon : 0,
      maxReconPrice: maxReconProvided ? maxRecon : 0,
      minMileage: minMileageProvided ? minMileage : 0,
      maxMileage: maxMileageProvided ? maxMileage : 0,
      minYear: minYearProvided ? minYear : 0,
      maxYear: maxYearProvided ? maxYear : 0,
    };

    const submitWatchListArea: DsLoadingAreaConfiguration = {
      id: 'bodyLoading',
      loadingText: 'Vehicle to watchlist',
      loadingTitle: 'Adding',
    };

    this.loadingAreaService.addLoadingArea(submitWatchListArea);
    this.dbConfigService.addVehicleWatch(watchItem).subscribe({
      next: (response) => {
        if (!response.success) {
          this.addToast(
            'critical',
            response.error?.message || 'Failed to add to watchlist.'
          );
          this.loadingAreaService.removeLoadingArea(submitWatchListArea.id);
          return;
        }
        this.addToast('positive', 'Vehicle added to watchlist successfully!');
        this.closeWatchlistModal();
        this.onWatchlistTabClick();
        this.loadingAreaService.removeLoadingArea(submitWatchListArea.id);
      },
      error: (err) => {
        this.addToast(
          'critical',
          `Failed to add to watchlist: ${err?.error || 'Unknown error'}`
        );
        this.loadingAreaService.removeLoadingArea(submitWatchListArea.id);
      },
    });
  }
  // Watchlist Modal
  watchlistForm: {
    vehicleType: string;
    modelYear: string;
    brand: string;
    model: string;
    fuelType: string;
    transmissionType: string;
    variant: string;
    adgCode: string;
    minReservePrice: string;
    maxReservePrice: string;
    minReconPrice: string;
    maxReconPrice: string;
    minMileage: string;
    maxMileage: string;
    minYear: string;
    maxYear: string;
  } = {
    vehicleType: '',
    modelYear: '',
    brand: '',
    model: '',
    fuelType: '',
    transmissionType: '',
    variant: '',
    adgCode: '',
    minReservePrice: '',
    maxReservePrice: '',
    minReconPrice: '',
    maxReconPrice: '',
    minMileage: '',
    maxMileage: '',
    minYear: '',
    maxYear: '',
  };

  awaitingVehicles: CompletedVehicle[] = [
    {
      vin: '',
      brand: '',
      description: '',
      model: '',
      year: 0,
      purchasingDealer: '',
      purchasingDealerName: '',
      purchasePrice: 0,
      matchBid: 0,
      expiryDate: '',
      guid: '',
      photographS3Keys: '',
      photographsList: [],
      conditionReportS3Key: '',
      conditionReportList: [],
    },
  ];

  // Tab click handlers
  onAuctionTabClick(): void {
    this.loadActiveVehicles(true);
  }
  onCompletedTabClick(): void {
    this.stopAuctionAutoRefresh();
    this.loadCompletedVehicles();
  }

  loadCompletedVehicles(): void {
    const completedLoadingArea: DsLoadingAreaConfiguration = {
      id: 'bodyLoading',
      loadingTitle: 'Loading',
      loadingText: 'Completed Vehicles',
    };
    this.loadingAreaService.addLoadingArea(completedLoadingArea);
    const agentCode = localStorage.getItem('dealerCode') || '';
    this.dbConfigService.getSellingVehicles(agentCode).subscribe({
      next: (response) => {
        if (!response.success) {
          this.addToast(
            'critical',
            response.error?.message || 'Failed to load completed vehicles.'
          );
          this.loadingAreaService.removeLoadingArea(completedLoadingArea.id);
          this.cdr.detectChanges();
          return;
        }
        const vehicles = response.data ?? [];
        console.log('Completed vehicles loaded:', vehicles);

        const completedRaw = vehicles.filter((v) => v.statusCode === 'VP');
        const awaitingRaw = vehicles.filter((v) => v.statusCode !== 'VP');

        const completedNameRequests = completedRaw.map((v) =>
          this.getDealerNameByCode(v.highestBidHolder)
        );
        const awaitingNameRequests = awaitingRaw.map((v) =>
          this.getDealerNameByCode(v.highestBidHolder)
        );

        forkJoin([
          completedNameRequests.length ? forkJoin(completedNameRequests) : of([] as string[]),
          awaitingNameRequests.length ? forkJoin(awaitingNameRequests) : of([] as string[]),
        ]).subscribe(([completedNames, awaitingNames]) => {
          this.completedVehicles = completedRaw.map((v, i) => ({
            vin: v.vin,
            brand: v.brand,
            description: v.variant,
            model: v.model,
            year: Number(v.modelYear),
            purchasingDealer: v.highestBidHolder,
            purchasingDealerName: completedNames[i] ?? '',
            purchasePrice: v.highestBid,
            matchBid: v.highestBid,
            expiryDate: this.formatExpiryTime(v.expiryTime),
            guid: v.guid,
            photographS3Keys: v.photographS3Keys,
            photographsList: v.photographsList ?? [],
            conditionReportS3Key: v.conditionReportS3Key,
            conditionReportList: v.conditionReportList ?? [],
          }));
          this.awaitingVehicles = awaitingRaw.map((v, i) => ({
            vin: v.vin,
            brand: v.brand,
            description: v.variant,
            model: v.model,
            year: Number(v.modelYear),
            purchasingDealer: v.highestBidHolder,
            purchasingDealerName: awaitingNames[i] ?? '',
            purchasePrice: v.highestBid,
            expiryDate: this.formatExpiryTime(v.expiryTime),
            matchBid: v.highestBid,
            guid: v.guid,
            photographS3Keys: v.photographS3Keys,
            photographsList: v.photographsList ?? [],
            conditionReportS3Key: v.conditionReportS3Key,
            conditionReportList: v.conditionReportList ?? [],
          }));

          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        this.addToast(
          'critical',
          `Failed to load completed vehicles: ${err?.error || 'Unknown error'}`
        );
        this.loadingAreaService.removeLoadingArea(completedLoadingArea.id);
        this.cdr.detectChanges();
      },
      complete: () => {
        this.loadingAreaService.removeLoadingArea(completedLoadingArea.id);
        this.cdr.detectChanges();
      },
    });
  }
  formatExpiryTime(expiryTime: string): string {
    // Parse string parts
    const year = +expiryTime.substring(0, 4);
    const month = +expiryTime.substring(4, 6) - 1; // Month is zero-based in JS Date
    const day = +expiryTime.substring(6, 8);
    const hour = +expiryTime.substring(8, 10);
    const minute = +expiryTime.substring(10, 12);
    const second = +expiryTime.substring(12, 14);

    const date = new Date(year, month, day, hour, minute, second);

    // Add 24 hours
    date.setTime(date.getTime() + 24 * 60 * 60 * 1000);

    // Format date to yyyy-MM-dd HH:mm:ss
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const HH = String(date.getHours()).padStart(2, '0');
    const MM = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd} ${HH}:${MM}:${ss}`;
  }

  onPurchasingTabClick(): void {
    const purchasingLoadingArea: DsLoadingAreaConfiguration = {
      id: 'bodyLoading',
      loadingText: 'Purchasing Vehicles',
      loadingTitle: 'Loading',
    };
    this.stopAuctionAutoRefresh();
    this.loadingAreaService.addLoadingArea(purchasingLoadingArea);
    this.dbConfigService
      .getPurchasingVehicles(localStorage.getItem('dealerCode') || '')
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.addToast(
              'critical',
              response.error?.message || 'Failed to load purchasing vehicles.'
            );
            this.loadingAreaService.removeLoadingArea(purchasingLoadingArea.id);
            this.cdr.detectChanges();
            return;
          }
          const vehicles = response.data ?? [];
          this.purchasingVehicles = vehicles.map((v) => ({
            vin: v.vin,
            brand: v.brand,
            description: v.variant,
            model: v.model,
            year: Number(v.modelYear),
            sellingDealer: v.loadedDealerName,
            purchasePrice: v.highestBid,
            guid: v.guid,
            conditionReportS3Key: v.conditionReportS3Key,
            conditionReportList: v.conditionReportList ?? [],
            status: this.getStatus(v),
            photographS3Keys: v.photographS3Keys ?? '',
            photographsList: v.photographsList ?? [],
          }));
          this.loadingAreaService.removeLoadingArea(purchasingLoadingArea.id);
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.addToast(
            'critical',
            `Failed to load purchasing vehicles: ${err?.error || 'Unknown error'}`
          );
          this.loadingAreaService.removeLoadingArea(purchasingLoadingArea.id);
          this.cdr.detectChanges();
        },
      });
  }

  onWatchlistTabClick(): void {
    this.loadingAreaService.addLoadingArea({
      id: 'bodyLoading',
      loadingText: 'Loading Watchlist',
    });
    this.stopAuctionAutoRefresh();
    const agent = this.userStateService.userId();
    const agentCode = localStorage.getItem('dealerCode') || '';
    const request = { agent, agentCode };

    this.dbConfigService.getWatchlist(request).subscribe({
      next: (response) => {
        if (!response.success) {
          this.addToast(
            'critical',
            response.error?.message || 'Failed to load watchlist.'
          );
          this.loadingAreaService.removeLoadingArea('bodyLoading');
          this.cdr.detectChanges();
          return;
        }
        const items = response.data ?? [];
        this.watchlistVehicles = items.map((item) => ({
          brand: item.brand ?? '',
          model: item.model ?? '',
          adgCode: item.adgCode ?? '',
          reservePrice: item.maxReservePrice ?? 0,
          reconPrice: item.maxReconPrice ?? 0,
          mileage: item.maxMileage ?? 0,
          year: item.maxYear ?? 0,
        }));
        this.cdr.detectChanges();
        this.loadingAreaService.removeLoadingArea('bodyLoading');
      },
      error: (err) => {
        this.addToast(
          'critical',
          `Failed to load watchlist: ${err?.error || 'Unknown error'}`
        );
        this.loadingAreaService.removeLoadingArea('bodyLoading');
        this.cdr.detectChanges();
      },
    });
  }

  purchasingVehicles: PurchasingVehicle[] = [
    {
      vin: '',
      brand: '',
      description: '',
      model: '',
      year: 0,
      sellingDealer: '',
      purchasePrice: 0,
      guid: '',
      conditionReportS3Key: '',
      conditionReportList: [],
      status: '',
      photographS3Keys: '',
      photographsList: [],
    },
  ];

  watchlistVehicles: WatchlistVehicle[] = [
    {
      brand: '',
      model: '',
      adgCode: '',
      reservePrice: 0,
      reconPrice: 0,
      mileage: 0,
      year: 0,
    },
  ];

  resetWatchlistForm() {
    this.watchlistForm = {
      vehicleType: '',
      modelYear: '',
      brand: '',
      model: '',
      fuelType: '',
      transmissionType: '',
      variant: '',
      adgCode: '',
      minReservePrice: '',
      maxReservePrice: '',
      minReconPrice: '',
      maxReconPrice: '',
      minMileage: '',
      maxMileage: '',
      minYear: '',
      maxYear: '',
    };
  }

  vehicles: Vehicle[] = [
    {
      id: '',
      brand: '',
      description: '',
      modelYear: 0,
      location: '',
      remainingTimeMs:
        2 * 24 * 60 * 60 * 1000 + 3 * 60 * 60 * 1000 + 35 * 60 * 1000, // 2d 3h 35m
      remainingTimeDisplay: '',
      reservePrice: 0,
      reconPrice: 0,
      currentBid: 0,
      highestBid: 0,
      vehicleImage: '',
      photographS3Keys: '',
      loadedDealerCode: '',
      photographsList: [],
      conditionReportS3Key: '',
      conditionReportList: [],
      loadedDealerName: '',
    },
  ];

  constructor() {
    this.startCountdown();
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    if (this.auctionRefreshInterval) {
      clearInterval(this.auctionRefreshInterval);
      this.auctionRefreshInterval = null;
    }
  }

  startCountdown(): void {
    this.countdownInterval = setInterval(() => {
      this.vehicles.forEach((vehicle) => {
        vehicle.remainingTimeMs -= 1000; // Decrease by 1 second
        if (vehicle.remainingTimeMs <= 0) {
          vehicle.remainingTimeMs = 0;
          vehicle.remainingTimeDisplay = 'ENDED';
        } else {
          vehicle.remainingTimeDisplay = this.formatTime(
            vehicle.remainingTimeMs
          );
        }
      });
    }, 1000);
  }

  formatTime(ms: number): string {
    const days = Math.floor(ms / (24 * 60 * 60 * 1000));
    const hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
    const seconds = Math.floor((ms % (60 * 1000)) / 1000);

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  onSearch(): void {
    this.searchClicked = true;
    console.log('Searching with:', {
      brand: this.selectedBrand(),
      location: this.selectedSeriesVariant(),
    });
  }

  async placeBid(vehicle: Vehicle, quickBid: number): Promise<void> {
    const bidLoadingArea: DsLoadingAreaConfiguration = {
      id: 'bodyLoading',
      loadingText: 'Placing Bid',
    };
    if (vehicle.loadedDealerCode === localStorage.getItem('dealerCode')) {
      this.addToast(
        'critical',
        'Bid cannot be placed: This vehicle was loaded by your dealership.'
      );
      return;
    }
    if (!this.canView) {
      this.addToast(
        'critical',
        'Bid cannot be placed: You do not have permission to Bid vehicles.'
      );
      return;
    }

    const quickBidAmount = quickBid;
    if (
      quickBidAmount <= vehicle.reservePrice ||
      quickBidAmount <= vehicle.currentBid
    ) {
      this.addToast(
        'critical',
        'You cannot place a bid less than reserve price or current bid'
      );
      return;
    }
    if (!quickBidAmount || quickBidAmount <= 0) {
      this.addToast('critical', 'Please enter a bid amount greater than 0.');
      return;
    }
    if (quickBidAmount <= vehicle.highestBid) {
      this.addToast(
        'critical',
        `You cannot place a bid less than the current highest bid of R${vehicle.highestBid.toLocaleString()}.`
      );
      return;
    }

    const bidRequest: BidRequest = {
      vin: vehicle.id,
      bidAmount: quickBidAmount,
      agentCode: localStorage.getItem('dealerCode') || '',
    };
    this.loadingAreaService.addLoadingArea(bidLoadingArea);
    this.dbConfigService.placeBid(bidRequest).subscribe({
      next: (response) => {
        if (!response.success) {
          this.addToast('critical', response.error?.message || 'Bid failed.');
          this.loadingAreaService.removeLoadingArea(bidLoadingArea.id);
          this.cdr.detectChanges();
          return;
        }
        vehicle.currentBid = quickBidAmount;
        this.addToast(
          'positive',
          `Quick bid of R${quickBidAmount.toLocaleString()} placed successfully for ${vehicle.description}!`
        );
        this.loadingAreaService.removeLoadingArea(bidLoadingArea.id);
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.addToast(
          'critical',
          `Bid failed: ${err?.error || 'Unknown error'}`
        );
        this.loadingAreaService.removeLoadingArea(bidLoadingArea.id);
        this.cdr.detectChanges();
      },
      complete: () => {
        this.loadingAreaService.removeLoadingArea(bidLoadingArea.id);
        this.cdr.detectChanges();
      },
    });
  }

  formatCurrency(amount: number): string {
    return `R${amount.toLocaleString('en-ZA')}`;
  }

  completedVehicles: CompletedVehicle[] = [
    {
      vin: '',
      brand: '',
      description: '',
      model: '',
      year: 0,
      guid: '',
      purchasingDealer: '',
      purchasingDealerName: '',
      purchasePrice: 0,
      matchBid: 0,
      expiryDate: '',
      photographS3Keys: '',
      photographsList: [],
      conditionReportS3Key: '',
      conditionReportList: [],
    },
  ];

  addToast(tone: 'critical' | 'positive' | 'neutral', teastText: string): void {
    this.toastService.pushToast({
      id: 'error-toast',
      tone: tone,
      toastText: teastText,
    } as DsToastModel);
  }
  onViewConditionReport(vehicle: CompletedVehicle) {
    // Fetch and download condition report from S3
    if (vehicle.conditionReportList && vehicle.conditionReportList.length > 0) {
      this.fetchAndDownloadConditionReport(
        vehicle.conditionReportList[0],
        vehicle.vin
      );
    } else if (vehicle.conditionReportS3Key) {
      this.fetchAndDownloadConditionReport(
        vehicle.conditionReportS3Key,
        vehicle.vin
      );
    } else {
      this.addToast('critical', 'No condition report found for this vehicle.');
    }
  }

  onViewConditionReportPurch(vehicle: {
    vin: string;
    brand: string;
    description: string;
    model: string;
    year: number;
    sellingDealer: string;
    purchasePrice: number;
    guid: string;
    conditionReportS3Key: string;
    conditionReportList: string[] | undefined;
    status: string;
  }) {
    // Fetch and download condition report from S3
    if (vehicle.conditionReportList && vehicle.conditionReportList.length > 0) {
      this.fetchAndDownloadConditionReport(
        vehicle.conditionReportList[0],
        vehicle.vin
      );
    } else if (vehicle.conditionReportS3Key) {
      this.fetchAndDownloadConditionReport(
        vehicle.conditionReportS3Key,
        vehicle.vin
      );
    } else {
      this.addToast('critical', 'No condition report found for this vehicle.');
    }
  }
  onViewConditionReportStr(base64String: string) {
    try {
      if (base64String.startsWith('data:')) {
        base64String = base64String.substring(base64String.indexOf(',') + 1);
      }
      base64String = base64String.replace(/\s/g, '');

      const uintArray = this.base64ToUint8Array(base64String);

      const blob = new Blob([uintArray], { type: 'application/pdf' });

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = 'condition-report.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    } catch (error) {
      this.addToast('critical', 'Failed to download the condition report.');
      console.error('Error downloading condition report:', error);
    }
  }

  private fetchAndDownloadConditionReport(s3Key: string, vin: string): void {
    this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
    this.dbConfigService.getConditionReport(vin).subscribe({
      next: (blob: Blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
        link.download = 'condition-report.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
      },
      error: (error) => {
        this.dbConfigService.getMediaByKey(s3Key).subscribe((blob: Blob) => {
          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
          link.download = 'condition-report.pdf';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
        });

        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
        this.addToast('critical', 'Failed to download the condition report.');
        console.error('Error downloading condition report:', error);
      },
    });
  }

  base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  onMatchHighestBid(vehicle: CompletedVehicle): void {
    const matchOfferLoadingArea: DsLoadingAreaConfiguration = {
      id: 'bodyLoading',
      loadingText: 'Matching Highest Bid',
    };
    if (!this.canView) {
      this.addToast(
        'critical',
        'Matching Highest Bid is not available for Viewer Role.'
      );
      return;
    }
    this.loadingAreaService.addLoadingArea(matchOfferLoadingArea);
    this.dbConfigService.matchOffer(vehicle.vin).subscribe({
      next: (response) => {
        if (!response.success) {
          this.addToast(
            'critical',
            response.error?.message || 'Failed to match offer.'
          );
          this.loadingAreaService.removeLoadingArea(matchOfferLoadingArea.id);
          this.cdr.detectChanges();
          return;
        }
        this.addToast('positive', 'Match offer sent successfully.');
        this.loadCompletedVehicles(); // Optionally refresh the list
        this.loadingAreaService.removeLoadingArea(matchOfferLoadingArea.id);
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.addToast(
          'critical',
          `Failed to match offer: ${err?.error || 'Unknown error'}`
        );
        this.loadingAreaService.removeLoadingArea(matchOfferLoadingArea.id);
        this.cdr.detectChanges();
      },
    });
  }

  OnConcludeBid(vehicle: CompletedVehicle): void {
    const matchOfferLoadingArea: DsLoadingAreaConfiguration = {
      id: 'bodyLoading',
      loadingText: 'Matching Highest Bid',
    };
    if (!this.canView) {
      this.addToast(
        'critical',
        'Matching Highest Bid is not available for Viewer Role.'
      );
      return;
    }
    this.loadingAreaService.addLoadingArea(matchOfferLoadingArea);
    this.dbConfigService.concludeDeal(vehicle.vin).subscribe({
      next: (response) => {
        if (!response.success) {
          this.addToast(
            'critical',
            response.error?.message || 'Failed to conclude offer.'
          );
          this.loadingAreaService.removeLoadingArea(matchOfferLoadingArea.id);
          this.cdr.detectChanges();
          return;
        }
        this.addToast('positive', 'Offer Concluded successfully.');
        this.loadCompletedVehicles(); // Optionally refresh the list
        this.loadingAreaService.removeLoadingArea(matchOfferLoadingArea.id);
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.addToast(
          'critical',
          `Failed to conclude offer: ${err?.error || 'Unknown error'}`
        );
        this.loadingAreaService.removeLoadingArea(matchOfferLoadingArea.id);
        this.cdr.detectChanges();
      },
    });
  }
  highestBidDisclaimer = '';
  highestBidDisclaimerModalOpen = false;
  highestBidVehicle: CompletedVehicle | null = null;
  onAcceptHighestBid(vehicle: CompletedVehicle) {
    this.highestBidVehicle = vehicle;
    this.highestBidDisclaimer = `By accepting the highest bid for vehicle VIN: ${vehicle.vin}, you agree to sell the vehicle at the offered price of R${vehicle.matchBid.toLocaleString()}. This action is final and cannot be undone. Please ensure all vehicle details and conditions are satisfactory before proceeding.`;
    this.highestBidDisclaimerModalOpen = true;
  }
  onAcceptHighestBidService(vehicle: CompletedVehicle | null) {
    const acceptOfferLoadingArea: DsLoadingAreaConfiguration = {
      id: 'bodyLoading',
      loadingText: 'Accepting Highest Bid',
    };
    if (!this.canView) {
      this.addToast(
        'critical',
        'Accepting Highest Bid is not available for Viewer Role.'
      );
      return;
    }
    this.loadingAreaService.addLoadingArea(acceptOfferLoadingArea);
    if (vehicle) {
      this.dbConfigService.acceptOffer(vehicle.vin).subscribe({
        next: (response) => {
          if (!response.success) {
            this.addToast(
              'critical',
              response.error?.message || 'Failed to accept offer.'
            );
            this.loadingAreaService.removeLoadingArea(
              acceptOfferLoadingArea.id
            );
            this.cdr.detectChanges();
            return;
          }
          this.highestBidDisclaimerModalOpen = false;
          this.addToast('positive', 'Offer accepted successfully.');
          this.loadCompletedVehicles(); // Optionally refresh the list
          this.loadingAreaService.removeLoadingArea(acceptOfferLoadingArea.id);
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.addToast(
            'critical',
            `Failed to accept offer: ${err?.error || 'Unknown error'}`
          );
          this.loadingAreaService.removeLoadingArea(acceptOfferLoadingArea.id);
          this.cdr.detectChanges();
        },
      });
    }
  }

  onCopyUniqueId(vehicle: CompletedVehicle) {
    this.clipboard.copy(vehicle.guid);
    this.addToast('positive', 'Copied ' + vehicle.guid + ' to clipboard.');
  }

  onCancelDeal(vehicle: CompletedVehicle) {
    const cancelLoadingArea: DsLoadingAreaConfiguration = {
      id: 'bodyLoading',
      loadingText: 'Cancelling Deal',
    };
    if (!this.canView) {
      this.addToast(
        'critical',
        'Cancelling Deal is not available for Viewer Role.'
      );
      return;
    }
    this.loadingAreaService.addLoadingArea(cancelLoadingArea);
    this.dbConfigService.cancelDeal(vehicle.vin).subscribe({
      next: (response) => {
        if (!response.success) {
          this.addToast(
            'critical',
            response.error?.message || 'Failed to cancel deal.'
          );
          this.loadingAreaService.removeLoadingArea(cancelLoadingArea.id);
          this.cdr.detectChanges();
          return;
        }
        this.addToast('positive', 'Cancelled successfully.');
        this.loadCompletedVehicles(); // Optionally refresh the list
        this.loadingAreaService.removeLoadingArea(cancelLoadingArea.id);
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.addToast(
          'critical',
          `Failed to cancel deal: ${err?.error || 'Unknown error'}`
        );
        this.loadingAreaService.removeLoadingArea(cancelLoadingArea.id);
        this.cdr.detectChanges();
      },
    });
  }

  getStatus(v: AuctionVehicle): string {
    console.log('AuctionVehicle: ', v);
    if((v.offerAction === 'A' || v.offerAction === 'M') &&
      v.statusCode !== 'OC'){
      return 'Deal accepted waiting for finalization';
    } else if(v.statusCode === 'VP'){
      return 'Deal Pending'
    }else {
      return '';
    }
  }
  onDeleteFromWatchList(vehicle: WatchlistVehicle) {
    const agent = this.userStateService.userId();
    const agentCode = localStorage.getItem('dealerCode') || '';
    const adgCode = vehicle.adgCode;

    const watchListRequest = { agent, agentCode, adgCode };
    this.dbConfigService.removeVehicleWatch(watchListRequest).subscribe({
      next: (response) => {
        if (!response.success) {
          this.addToast(
            'critical',
            response.error?.message || 'Failed to remove from watchlist.'
          );
          return;
        }
        this.addToast('positive', 'Vehicle removed from watchlist.');
        this.onWatchlistTabClick();
      },
      error: (err) => {
        this.addToast(
          'critical',
          `Failed to remove from watchlist: ${err?.error || 'Unknown error'}`
        );
      },
    });
  }
  // Add these properties to your component
  loadingDropdown = {
    modelYear: false,
    brand: false,
    model: false,
    fuelType: false,
    transmissionType: false,
    variant: false,
  };
  loadingLabel = '';

  // Vehicle Type Change
  onVehicleTypeChange(vehicleType: string) {
    this.loadingDropdown.modelYear = true;
    this.loadingLabel = 'Loading Model Years...';
    this.transunionService.getVehicleTypeYears(vehicleType).subscribe({
      next: (response) => {
        if (!response.success) {
          this.addToast(
            'critical',
            response.error?.message || 'Failed to load model years.'
          );
          return;
        }
        this.modelYearOptions =
          response.data?.lookupList.lookup.map((y) => ({
            id: y.description,
            label: y.description,
            value: y.description,
          })) ?? [];
        this.watchlistForm.modelYear = '';
        this.brandOptions = [];
        this.modelOptions = [];
        this.fuelTypeOptions = [];
        this.transmissionTypeOptions = [];
        this.variantOptions = [];
      },
      complete: () => {
        this.loadingDropdown.modelYear = false;
        this.loadingLabel = '';
      },
    });
  }

  // Model Year Change
  onModelYearChange(vehicleType: string, year: string) {
    this.loadingDropdown.brand = true;
    this.loadingLabel = 'Loading Brands...';
    this.transunionService.getVehicleTypeBrands(vehicleType, year).subscribe({
      next: (response) => {
        if (!response.success) {
          this.addToast(
            'critical',
            response.error?.message || 'Failed to load brands.'
          );
          return;
        }
        this.brandOptions =
          response.data?.lookupList.lookup.map((b) => ({
            id: b.description,
            label: b.description,
            value: b.description,
          })) ?? [];
        this.watchlistForm.brand = '';
        this.modelOptions = [];
        this.fuelTypeOptions = [];
        this.transmissionTypeOptions = [];
        this.variantOptions = [];
      },
      complete: () => {
        this.loadingDropdown.brand = false;
        this.loadingLabel = '';
      },
    });
  }

  // Brand Change
  onBrandChange(vehicleType: string, year: string, brand: string) {
    this.loadingDropdown.model = true;
    this.loadingLabel = 'Loading Models...';
    this.transunionService
      .getVehicleTypeModels(vehicleType, year, brand)
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.addToast(
              'critical',
              response.error?.message || 'Failed to load models.'
            );
            return;
          }
          this.modelOptions =
            response.data?.lookupList.lookup.map((m) => ({
              id: m.description,
              label: m.description,
              value: m.description,
            })) ?? [];
          this.watchlistForm.model = '';
          this.fuelTypeOptions = [];
          this.transmissionTypeOptions = [];
          this.variantOptions = [];
        },
        complete: () => {
          this.loadingDropdown.model = false;
          this.loadingLabel = '';
        },
      });
  }

  // Model Change
  onModelChange(
    vehicleType: string,
    year: string,
    brand: string,
    model: string
  ) {
    this.loadingDropdown.fuelType = true;
    this.loadingLabel = 'Loading Fuel Types...';
    this.transunionService
      .getVehicleTypeFuelTypes(vehicleType, year, brand, model.toUpperCase())
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.addToast(
              'critical',
              response.error?.message || 'Failed to load fuel types.'
            );
            return;
          }
          if (
            !response.data?.lookupList.lookup ||
            response.data.lookupList.lookup.length === 0
          ) {
            // If no fuel types are returned, default to Petrol and Diesel
            this.fuelTypeOptions = [
              { id: 'P', label: 'Petrol', value: 'P' },
              { id: 'D', label: 'Diesel', value: 'D' },
            ];
          } else {
            this.fuelTypeOptions =
              response.data?.lookupList.lookup.map((f: any) => {
                const fuelTypeMap: { [key: string]: string } = {
                  P: 'Petrol',
                  D: 'Diesel',
                  H: 'Hybrid',
                };
                const description =
                  fuelTypeMap[f.description.toUpperCase()] || f.description;
                return {
                  id: f.description,
                  label: description,
                  value: f.description,
                };
              }) ?? [];
          }
          this.watchlistForm.fuelType = '';
          this.transmissionTypeOptions = [];
          this.variantOptions = [];
        },
        complete: () => {
          this.loadingDropdown.fuelType = false;
          this.loadingLabel = '';
        },
      });
  }

  // Fuel Type Change
  onFuelTypeChange(
    vehicleType: string,
    year: string,
    brand: string,
    model: string,
    fuelType: string
  ) {
    this.loadingDropdown.transmissionType = true;
    this.loadingLabel = 'Loading Transmission Types...';
    this.transunionService
      .getVehicleTypeTransmissionTypes(
        vehicleType,
        year,
        brand,
        model.toUpperCase(),
        fuelType
      )
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.addToast(
              'critical',
              response.error?.message || 'Failed to load transmission types.'
            );
            return;
          }
          this.transmissionTypeOptions =
            response.data?.lookupList.lookup.map((f: any) => {
              const fuelTypeMap: { [key: string]: string } = {
                A: 'Automatic',
                M: 'Manual',
              };
              const description =
                fuelTypeMap[f.description.toUpperCase()] || f.description;
              return {
                id: f.description,
                label: description,
                value: f.description,
              };
            }) ?? [];
          this.watchlistForm.transmissionType = '';
          this.variantOptions = [];
        },
        complete: () => {
          this.loadingDropdown.transmissionType = false;
          this.loadingLabel = '';
        },
      });
  }

  // Transmission Type Change
  onTransmissionTypeChange(
    vehicleType: string,
    year: string,
    brand: string,
    model: string,
    fuelType: string,
    transmissionType: string
  ) {
    this.loadingDropdown.variant = true;
    this.loadingLabel = 'Loading Variants...';
    this.transunionService
      .getVehicleTypeVariants(
        vehicleType,
        year,
        brand,
        model.toUpperCase(),
        fuelType,
        transmissionType
      )
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.addToast(
              'critical',
              response.error?.message || 'Failed to load variants.'
            );
            return;
          }
          this.variantOptions =
            response.data?.variantList.variant.map((v) => ({
              id: v.variantDescription,
              label: v.variantDescription,
              value: v.mmCode,
            })) ?? [];
          this.watchlistForm.variant = '';
        },
        complete: () => {
          this.loadingDropdown.variant = false;
          this.loadingLabel = '';
        },
      });
  }

  brands(): { id: string; label: string; value: string }[] {
    return this.brandOptions;
  }

  seriesVariants(): { id: string; label: string; value: string }[] {
    return this.variantOptions;
  }
  onVariantChange(variantDescription: string | string[] | null) {
    const description = Array.isArray(variantDescription)
      ? variantDescription[0]
      : variantDescription;

    const selectedVariant = this.variantOptions.find(
      (v) => v.id === description || v.label === description
    );
    // If your API provides adgCode, use it; otherwise, use mmCode

    this.watchlistForm.adgCode = selectedVariant?.value || '';
  }
  quickBidMap: { [key: string]: number } = {};
  placeBidModalIsOpen: boolean = false;

  addQuickBid(vehicle: any) {
    if (vehicle.loadedDealerCode === localStorage.getItem('dealerCode')) {
      this.addToast(
        'critical',
        'Bid cannot be placed: This vehicle was loaded by your dealership.'
      );
      return;
    }
    let currentBid = this.quickBidMap[vehicle.id] || 0;
    console.log('currentBid', currentBid);

    if (currentBid === 0) {
      if (vehicle.currentBid <= vehicle.reservePrice) {
        currentBid = vehicle.reservePrice;
      } else {
        currentBid = vehicle.highestBid;
      }
    }
    this.quickBidMap[vehicle.id] = currentBid + 1000;
  }

  subtractQuickBid(vehicle: Vehicle) {
    if (vehicle.loadedDealerCode === localStorage.getItem('dealerCode')) {
      this.addToast(
        'critical',
        'Bid cannot be placed: This vehicle was loaded by your dealership.'
      );
      return;
    }
    const currentBid = this.quickBidMap[vehicle.id] || 0;
    const minBid = Math.max(vehicle.highestBid, vehicle.reservePrice);
    const newBid = Math.max(minBid, currentBid - 1000);
    this.quickBidMap[vehicle.id] = newBid;
  }
  BidModalVehicle: Vehicle = {
    id: '',
    brand: '',
    description: '',
    modelYear: 0,
    location: '',
    remainingTimeMs: 0,
    remainingTimeDisplay: '',
    reservePrice: 0,
    reconPrice: 0,
    currentBid: 0,
    vehicleImage: '',
    photographS3Keys: '',
    highestBid: 0,
    loadedDealerCode: '',
    photographsList: [],
    conditionReportS3Key: '',
    conditionReportList: [],
    loadedDealerName: '',
  };
  BidModalCompletedVehicle: CompletedVehicle = {
    vin: '',
    brand: '',
    description: '',
    model: '',
    year: 0,
    guid: '',
    purchasingDealer: '',
    purchasingDealerName: '',
    purchasePrice: 0,
    matchBid: 0,
    expiryDate: '',
    photographS3Keys: '',
    photographsList: [],
    conditionReportS3Key: '',
    conditionReportList: [],
  };
  BidModalPurchasingVehicle: PurchasingVehicle = {
    vin: '',
    brand: '',
    description: '',
    model: '',
    year: 0,
    sellingDealer: '',
    purchasePrice: 0,
    guid: '',
    conditionReportS3Key: '',
    conditionReportList: [],
    status: '',
    photographS3Keys: '',
    photographsList: [],
  };
  modalBidAmount: number = 0;
  openPlacebidModal(vehicle: Vehicle) {
    if (vehicle.loadedDealerCode === localStorage.getItem('dealerCode')) {
      this.addToast(
        'critical',
        'Bid cannot be placed: This vehicle was loaded by your dealership.'
      );
      return;
    }
    this.BidModalVehicle = vehicle;
    this.placeBidModalIsOpen = true;
  }
  closePlaceBidModal() {
    this.placeBidModalIsOpen = false;
  }

  decrementBidAmount(BidModalVehicle: Vehicle) {
    let currentBid = this.modalBidAmount || 0;

    if (currentBid === 0) {
      currentBid = Math.max(
        BidModalVehicle.highestBid,
        BidModalVehicle.reservePrice
      );
    }

    const minBid = Math.max(
      BidModalVehicle.highestBid,
      BidModalVehicle.reservePrice
    );
    this.modalBidAmount = Math.max(minBid, currentBid - 500);
  }

  incrementBidAmount(BidModalVehicle: Vehicle) {
    let currentBid = this.modalBidAmount || 0;

    if (currentBid === 0) {
      currentBid = Math.max(
        BidModalVehicle.highestBid,
        BidModalVehicle.reservePrice
      );
    }

    this.modalBidAmount = currentBid + 500;
  }

  showNoPhotographsToast(): void {
    this.addToast('critical', 'No photographs available for this vehicle.');
  }
  photographsModalIsOpen: boolean = false;
  photographsList: VehicleFile[] = [];

  openPhotographsModal(vehicle: Vehicle) {
    this.currentPhotoIndex = 0;
    if (vehicle.photographsList && vehicle.photographsList.length > 0) {
      // Load photos from S3 keys
      this.loadPhotographsFromS3(vehicle.photographsList);
      this.photographsModalIsOpen = true;
    } else if (vehicle.photographS3Keys) {
      // Parse the JSON string containing S3 keys
      try {
        const keys = JSON.parse(vehicle.photographS3Keys);
        if (Array.isArray(keys) && keys.length > 0) {
          this.loadPhotographsFromS3(keys);
          this.photographsModalIsOpen = true;
        } else {
          this.showNoPhotographsToast();
        }
      } catch (error) {
        console.error('Error parsing S3 keys:', error);
        this.showNoPhotographsToast();
      }
    } else {
      this.photographsList = [];
      this.showNoPhotographsToast();
    }
  }

  private loadPhotographsFromS3(s3Keys: string[]): void {
    // For each S3 key, fetch the blob and convert to data URL
    this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
    const photoPromises = s3Keys.map(
      (key) =>
        new Promise<VehicleFile>((resolve, reject) => {
          this.dbConfigService.getMediaByKey(key).subscribe({
            next: (blob: Blob) => {
              const reader = new FileReader();
              reader.onload = () => {
                resolve({
                  XSTRING: reader.result as string, // This is the data URL
                  FILENAME: key.split('/').pop() || 'photo.jpg',
                });
              };
              this.loadingAreaService.removeLoadingArea(
                this.loadingAreaConfig.id
              );
              reader.onerror = () => reject(reader.error);
              reader.readAsDataURL(blob);
            },
            error: (error) => {
              this.loadingAreaService.removeLoadingArea(
                this.loadingAreaConfig.id
              );
              console.error(`Error loading photograph from key ${key}:`, error);
              reject(error);
            },
          });
        })
    );

    Promise.all(photoPromises)
      .then((photos) => {
        this.photographsList = photos;
      })
      .catch((error) => {
        console.error('Error loading all photographs:', error);
        this.showNoPhotographsToast();
      });
  }
  base64ToFile(base64: string, filename: string, mimeType: string): File {
    const byteString = atob(base64);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new File([ab], filename, { type: mimeType });
  }
  currentPhotoIndex: number = 0;
  getPhotographImageUrl(photo: VehicleFile | undefined): string {
    if (!photo?.XSTRING) return '';

    // XSTRING now contains the data URL from blob conversion
    // Return it directly for display in img tags
    return photo.XSTRING;
  }

  closePhotographsModal() {
    this.photographsModalIsOpen = false;
  }

  vehicleCheckDetailsRequest: VehicleCheckDetaisRequest = {
    vin: '',
  };

  initialVehicleCheckDetaisResponse: VehicleCheckDetaisResponse = {
    vin: '',
    hasKeySets: false,
    hasTyreThread: false,
    hasFullServiceHistory: false,
    hasRunningWarranty: false,
    hasMotorplan: false,
    registeredInCustomersName: false,
    involvedInAccident: false,
    ciaComments: false,
  };
  protected vehicleOverviewForCompletedModalIsOpen: boolean = false;
  protected vehicleOverviewForPurchasingModalIsOpen: boolean = false;

  openOverviewModal(vehicle: Vehicle) {
    this.BidModalVehicle = vehicle;
    this.vehicleCheckDetailsRequest.vin = this.BidModalVehicle.id;

    this.dbConfigService
      .getActiveVehiclesCheckDetails(this.vehicleCheckDetailsRequest)
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.addToast(
              'critical',
              response.error?.message || 'Failed to load vehicle details.'
            );
            return;
          }
          this.initialVehicleCheckDetaisResponse =
            response.data ?? this.initialVehicleCheckDetaisResponse;
          console.log('Vehicle Check Details Response:', response);
          this.overviewModalIsOpen = true;
        },
        error: (err) => {
          this.addToast(
            'critical',
            `Failed to load vehicle details: ${err?.error || 'Unknown error'}`
          );
        },
      });
  }

  openOverviewModalforCompletedVehicle(vehicle: CompletedVehicle) {
    this.BidModalCompletedVehicle = vehicle;
    this.vehicleCheckDetailsRequest.vin = vehicle.vin;

    this.dbConfigService
      .getActiveVehiclesCheckDetails(this.vehicleCheckDetailsRequest)
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.addToast(
              'critical',
              response.error?.message || 'Failed to load vehicle details.'
            );
            return;
          }
          this.initialVehicleCheckDetaisResponse =
            response.data ?? this.initialVehicleCheckDetaisResponse;
          console.log('Vehicle Check Details Response:', response);
          this.vehicleOverviewForCompletedModalIsOpen = true;
        },
        error: (err) => {
          this.addToast(
            'critical',
            `Failed to load vehicle details: ${err?.error || 'Unknown error'}`
          );
        },
      });
  }

  openOverviewModalForPurchasingVehicle(vehicle: PurchasingVehicle) {
    this.BidModalPurchasingVehicle = vehicle;
    this.vehicleCheckDetailsRequest.vin = vehicle.vin;

    this.dbConfigService
      .getActiveVehiclesCheckDetails(this.vehicleCheckDetailsRequest)
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.addToast(
              'critical',
              response.error?.message || 'Failed to load vehicle details.'
            );
            return;
          }
          this.initialVehicleCheckDetaisResponse =
            response.data ?? this.initialVehicleCheckDetaisResponse;
          console.log('Vehicle Check Details Response:', response);
          this.vehicleOverviewForPurchasingModalIsOpen = true;
        },
        error: (err) => {
          this.addToast(
            'critical',
            `Failed to load vehicle details: ${err?.error || 'Unknown error'}`
          );
        },
      });
  }

  closeOverviewModal() {
    this.overviewModalIsOpen = false;
    this.vehicleOverviewForCompletedModalIsOpen = false;
    this.vehicleOverviewForPurchasingModalIsOpen = false;
  }

  protected openPhotographsForCompletedModal(vehicle: CompletedVehicle) {
    this.currentPhotoIndex = 0;
    if (vehicle.photographsList && vehicle.photographsList.length > 0) {
      // Load photos from S3 keys
      this.loadPhotographsFromS3(vehicle.photographsList);
      this.photographsModalIsOpen = true;
    } else if (vehicle.photographS3Keys) {
      // Parse the JSON string containing S3 keys
      try {
        const keys = JSON.parse(vehicle.photographS3Keys);
        if (Array.isArray(keys) && keys.length > 0) {
          this.loadPhotographsFromS3(keys);
          this.photographsModalIsOpen = true;
        } else {
          this.showNoPhotographsToast();
        }
      } catch (error) {
        console.error('Error parsing S3 keys:', error);
        this.showNoPhotographsToast();
      }
    } else {
      this.photographsList = [];
      this.showNoPhotographsToast();
    }
  }

  protected openPhotographsForPurchasingModal(vehicle: PurchasingVehicle) {
    this.currentPhotoIndex = 0;
    if (vehicle.photographsList && vehicle.photographsList.length > 0) {
      this.loadPhotographsFromS3(vehicle.photographsList);
      this.photographsModalIsOpen = true;
    } else if (vehicle.photographS3Keys) {
      try {
        const keys = JSON.parse(vehicle.photographS3Keys);
        if (Array.isArray(keys) && keys.length > 0) {
          this.loadPhotographsFromS3(keys);
          this.photographsModalIsOpen = true;
        } else {
          this.showNoPhotographsToast();
        }
      } catch (error) {
        console.error('Error parsing S3 keys:', error);
        this.showNoPhotographsToast();
      }
    } else {
      this.photographsList = [];
      this.showNoPhotographsToast();
    }
  }
  getDealerNameByCode(dealerCode: string): Observable<string> {
    if (!dealerCode) {
      return of('');
    }

    return this.dbConfigService.getDealerNameVyCode(dealerCode).pipe(
      map((response) => response.data ?? ''),
      catchError((err: unknown) => {
        console.error('Failed to load dealer name:', err);
        this.addToast('critical', 'Failed to load dealer name.');
        return of('');
      })
    );
  }

  protected isConcludable(vehicle: CompletedVehicle): boolean{
    return vehicle.purchasingDealer === localStorage.getItem('dealerCode');
  }

  protected isPushToHighBid(vehicle: CompletedVehicle): boolean {
    return vehicle.purchasingDealer !== localStorage.getItem('dealerCode');
  }
}
